import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  Breadcrumbs,
  Link,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Link as LinkIcon,
  DownloadOutlined as DownloadOutlinedIcon,
  Description as DocumentIcon,
  VideoLibrary as VideoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import AlertItem from '../../Components/AlertItem';
import {
  getDocumentBundle,
  updateDocumentBundle,
  updateBundleDocument,
  addDocumentToBundle,
  getDocumentList,
  downloadBundle,
} from '../../endpoints/documents';
import { applications } from '../../utils/variables';

const DocumentBundle: React.FC = () => {
  const navigate = useNavigate();
  const { bundleId } = useParams<{ bundleId: string }>();

  // State for editing bundle information
  const [isEditingBundle, setIsEditingBundle] = useState(false);
  const [bundleFormData, setBundleFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });

  // State for editing document orders
  const [editingOrder, setEditingOrder] = useState<string | null>(null);
  const [orderValue, setOrderValue] = useState<string>('');

  // State for adding documents to bundle
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [isAddingDocument, setIsAddingDocument] = useState(false);

  // State for download error handling
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Fetch bundle data
  const {
    data: bundle,
    isLoading,
    error,
  } = useApiGet(
    ['document-bundle', bundleId],
    () => getDocumentBundle(bundleId!),
    { enabled: !!bundleId }
  );

  // Download bundle mutation
  const downloadBundleMutation = useApiSend(
    downloadBundle,
    (blob: Blob) => {
      // Success handler - create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bundle?.name || 'bundle'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    (error: any) => {
      // Error handler
      console.error('Error downloading bundle:', error);

      // Handle GenericResponse error format
      if (error?.response?.data?.message) {
        setDownloadError(error.response.data.message);
      } else if (error?.message) {
        setDownloadError(error.message);
      } else {
        setDownloadError('Failed to download bundle. Please try again.');
      }
    }
  );

  // Update bundle form data when bundle loads
  useEffect(() => {
    if (bundle) {
      setBundleFormData({
        name: bundle.name,
        description: bundle.description || '',
        is_active: true,
      });
    }
  }, [bundle]);

  const canEditBundle = useCallback(() => {
    let apps = localStorage.getItem('apps');
    if (apps) {
      return apps.includes(applications.DocumentAdmin);
    }
    return false;
  }, []);

  // Sort documents by order (with null check) and filter inactive documents if user can't edit
  const sortedDocuments = useMemo(() => {
    if (!bundle?.documents || !Array.isArray(bundle.documents)) {
      return [];
    }

    const canEdit = canEditBundle();

    const filtered = bundle.documents.filter((document) => {
      // If user can edit, show all documents (active and inactive)
      // If user can't edit, show only active documents
      return canEdit ? true : document.is_active;
    });

    const sorted = filtered.sort((a, b) => (a.order || 0) - (b.order || 0));

    return sorted;
  }, [bundle, canEditBundle]);

  const handleDownloadBundle = () => {
    if (!bundleId) return;

    setDownloadError(null);
    downloadBundleMutation.mutate(bundleId);
  };

  const handleEditBundle = () => {
    setIsEditingBundle(true);
    setBundleFormData({
      name: bundle?.name || '',
      description: bundle?.description || '',
      is_active: true,
    });
  };

  const handleSaveBundle = async () => {
    try {
      const result = await updateDocumentBundle(bundleId!, bundleFormData);

      // Handle the response safely
      if (result && result.message) {
        alert(result.message);
      } else {
        alert('Bundle updated successfully!');
      }

      setIsEditingBundle(false);
      // Refresh the bundle data
      window.location.reload();
    } catch (error) {
      console.error('Error updating bundle:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to update bundle: ${errorMessage}`);
    }
  };

  const handleCancelEditBundle = () => {
    setIsEditingBundle(false);
    setBundleFormData({
      name: bundle?.name || '',
      description: bundle?.description || '',
      is_active: true,
    });
  };

  const handleEditOrder = (documentId: string, currentOrder: number) => {
    setEditingOrder(documentId);
    setOrderValue(currentOrder.toString());
  };

  const handleSaveOrder = async (documentId: string) => {
    const newOrder = parseInt(orderValue);
    if (isNaN(newOrder) || newOrder < 0) {
      alert('Please enter a valid positive number for the order.');
      return;
    }

    try {
      // Get the current document and validate the change
      const currentDocument = bundle?.documents.find(
        (doc) => doc.id === documentId
      );
      if (!currentDocument) {
        alert('Document not found in bundle.');
        return;
      }

      const currentOrder = currentDocument.order;

      // If the order hasn't changed, do nothing
      if (currentOrder === newOrder) {
        setEditingOrder(null);
        setOrderValue('');
        return;
      }

      // Validate that the new order doesn't exceed the total number of documents
      if (newOrder > bundle!.documents.length) {
        alert(
          `Order cannot exceed ${bundle!.documents.length}. Please enter a valid order number.`
        );
        return;
      }

      // Use the dedicated updateBundleDocument API for updating order
      // This is much cleaner than the previous remove + re-add approach
      await updateBundleDocument(bundleId!, {
        document_id: documentId,
        order: newOrder,
        removed: false, // We're updating the order, not removing the document
      });

      setEditingOrder(null);
      setOrderValue('');
      // Refresh the bundle data to show the updated order
      window.location.reload();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order. Please try again.');
    }
  };

  const handleCancelEditOrder = () => {
    setEditingOrder(null);
    setOrderValue('');
  };

  const handleRemoveDocument = async (documentId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to remove this document from the bundle?'
      )
    ) {
      return;
    }

    try {
      // Get the current document to get its order
      const currentDocument = bundle?.documents.find(
        (doc) => doc.id === documentId
      );
      if (!currentDocument) {
        alert('Document not found in bundle.');
        return;
      }

      // Use the dedicated updateBundleDocument API for removing documents
      const result = await updateBundleDocument(bundleId!, {
        document_id: documentId,
        order: currentDocument.order, // Send the actual current order
        removed: true, // This will remove the document from the bundle
      });

      // Handle the response safely
      if (result && result.message) {
        alert(result.message);
      } else {
        alert('Document removed from bundle successfully!');
      }

      // Refresh the bundle data
      window.location.reload();
    } catch (error) {
      console.error('Error removing document:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to remove document: ${errorMessage}`);
    }
  };

  const handleSearchDocuments = async () => {
    if (!documentSearchTerm.trim()) {
      alert('Please enter a search term.');
      return;
    }

    if (!bundle) {
      alert('Bundle data is not loaded. Please wait and try again.');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      console.log(
        'Searching for documents with term:',
        documentSearchTerm.trim()
      );

      const results = await getDocumentList({
        search: documentSearchTerm.trim(),
      });

      console.log('Search results:', results);
      console.log('Current bundle documents:', bundle.documents);

      // Filter out documents that are already in the bundle
      const filteredResults = results.data.filter(
        (doc) =>
          !bundle.documents.some(
            (bundleDoc) => bundleDoc.document_id === doc.id
          )
      );

      console.log('Filtered results:', filteredResults);

      setSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching documents:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to search documents: ${errorMessage}`);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddDocumentToBundle = async () => {
    if (!selectedDocument) {
      alert('Please select a document to add.');
      return;
    }

    if (!bundleId) {
      alert('Bundle ID is missing. Please refresh the page and try again.');
      return;
    }

    if (!bundle) {
      alert('Bundle data is not loaded. Please wait and try again.');
      return;
    }

    setIsAddingDocument(true);

    try {
      console.log('Adding document to bundle using addDocumentToBundle:', {
        bundleId,
        selectedDocument,
        bundleDocuments: bundle.documents,
      });

      // Calculate the default order (end of current list)
      const defaultOrder = (bundle.documents.length || 0) + 1;

      console.log('Calculated order:', defaultOrder);

      // Use the addDocumentToBundle API to add the document
      const result = await addDocumentToBundle(bundleId, {
        document_id: selectedDocument.id,
        order: defaultOrder,
      });

      console.log('API response:', result);

      // Handle the response safely
      if (result && result.message) {
        alert(result.message);
      } else {
        alert('Document added to bundle successfully!');
      }

      // Close modal and refresh data
      setShowAddDocumentModal(false);
      setSelectedDocument(null);
      setDocumentSearchTerm('');
      setSearchResults([]);
      setHasSearched(false);

      // Refresh the bundle data
      window.location.reload();
    } catch (error) {
      console.error('Error adding document to bundle:', error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`Failed to add document to bundle: ${errorMessage}`);
    } finally {
      setIsAddingDocument(false);
    }
  };

  const handleOpenAddDocumentModal = () => {
    setShowAddDocumentModal(true);
    setSelectedDocument(null);
    setDocumentSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const handleCloseAddDocumentModal = () => {
    setShowAddDocumentModal(false);
    setSelectedDocument(null);
    setDocumentSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
  };

  const getFileIcon = (document: any) => {
    if (document.video_url) {
      return <VideoIcon />;
    } else if (document.link_url) {
      return <LinkIcon />;
    } else if (document.downloadable) {
      return <DownloadOutlinedIcon />;
    } else {
      return <DocumentIcon />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading bundle...</Typography>
      </Box>
    );
  }

  if (error || !bundle) {
    return (
      <Box sx={{ p: 3 }}>
        <AlertItem
          severity="error"
          description="Error loading bundle"
          onClose={() => {}}
          keepOpen={true}
        />
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading bundle...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, pb: 14 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/library"
          color="inherit"
          underline="hover"
        >
          Documents
        </Link>
        <Typography color="text.primary">Bundle</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {bundle.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Bundle Information */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">Bundle Information</Typography>
                {canEditBundle() && (
                  <IconButton
                    onClick={handleEditBundle}
                    sx={{ color: '#0072CE' }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>

              {isEditingBundle ? (
                <Box>
                  <TextField
                    fullWidth
                    label="Bundle Name"
                    value={bundleFormData.name}
                    onChange={(e) =>
                      setBundleFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={bundleFormData.description || ''}
                    onChange={(e) =>
                      setBundleFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    multiline
                    rows={3}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleSaveBundle}
                      sx={{
                        backgroundColor: '#00A859',
                        '&:hover': { backgroundColor: '#008a4a' },
                      }}
                    >
                      Save
                    </Button>
                    <Button variant="outlined" onClick={handleCancelEditBundle}>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {bundle.description || 'No description available'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bundle?.documents?.length || 0} document(s) in this bundle
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          {/* Documents List */}
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Documents in Bundle
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Documents are listed in the order they should be reviewed or
                    used.
                  </Typography>
                </Box>
                {canEditBundle() && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenAddDocumentModal}
                    sx={{
                      backgroundColor: '#00A859',
                      '&:hover': { backgroundColor: '#008a4a' },
                    }}
                  >
                    Add
                  </Button>
                )}
              </Box>

              {sortedDocuments.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {isLoading
                      ? 'Loading documents...'
                      : bundle?.documents?.length === 0
                        ? 'No documents in this bundle yet.'
                        : 'No documents available to display.'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {sortedDocuments.map((document, index) => (
                    <React.Fragment key={document.id}>
                      <ListItem
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        {/* Header with order number and edit controls - only show if user can edit */}
                        {canEditBundle() && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {editingOrder === document.id ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <TextField
                                    size="small"
                                    value={orderValue}
                                    onChange={(e) =>
                                      setOrderValue(e.target.value)
                                    }
                                    inputProps={{
                                      style: { textAlign: 'center', width: 40 },
                                      min: 0,
                                      type: 'number',
                                    }}
                                    sx={{ width: 60 }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveOrder(document.id)}
                                    sx={{ color: '#00A859' }}
                                  >
                                    <SaveIcon />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEditOrder}
                                    sx={{ color: 'inherit' }}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Box>
                              ) : (
                                <>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      backgroundColor: 'primary.main',
                                      color: '#fff',
                                      borderRadius: '50%',
                                      width: 24,
                                      height: 24,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    {document.order}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditOrder(
                                        document.id,
                                        document.order
                                      )
                                    }
                                    sx={{ color: '#0072CE' }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </>
                              )}
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {getFileIcon(document)}
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Order {document.order}
                                </Typography>
                              </Box>
                            </Box>

                            <Tooltip title="Remove from Bundle">
                              <IconButton
                                onClick={() =>
                                  handleRemoveDocument(document.id)
                                }
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        {/* Simple header for non-editors - just file icon */}
                        {!canEditBundle() && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            {getFileIcon(document)}
                          </Box>
                        )}

                        {/* Document Name */}
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{
                            mb: 1,
                            fontWeight: 'medium',
                            color: 'text.primary',
                          }}
                        >
                          {document.name}
                        </Typography>

                        {/* Document Description */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {document.description || 'No description available'}
                        </Typography>

                        {/* Link to Document View */}
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-start' }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<LinkIcon />}
                            onClick={() => {
                              if (canEditBundle()) {
                                navigate(
                                  `/library/${document.document_id}/edit`
                                );
                              } else {
                                navigate(
                                  `/library/${document.document_id}/view/${document.version_id}`
                                );
                              }
                            }}
                            sx={{
                              color: '#0072CE',
                              borderColor: '#0072CE',
                              '&:hover': {
                                borderColor: '#005a9e',
                                backgroundColor: 'rgba(0, 114, 206, 0.04)',
                              },
                            }}
                          >
                            {canEditBundle()
                              ? 'Edit Document'
                              : 'View Document'}
                          </Button>
                        </Box>
                      </ListItem>
                      {index < sortedDocuments.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={handleDownloadBundle}
                  disabled={downloadBundleMutation.isPending}
                >
                  {downloadBundleMutation.isPending
                    ? 'Downloading...'
                    : 'Download Bundle'}
                </Button>
                {downloadError && (
                  <AlertItem
                    severity="error"
                    description={downloadError}
                    onClose={() => setDownloadError(null)}
                    keepOpen={true}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Bundle Summary */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Bundle Summary
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Documents
                  </Typography>
                  <Typography variant="h4" color="primary">
                    {bundle?.documents?.length || 0}
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    File Types
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {bundle?.documents?.length || 0} document(s) in bundle
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Document Modal */}
      <Dialog
        open={showAddDocumentModal}
        onClose={handleCloseAddDocumentModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Document to Bundle</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search for documents to add to this bundle. Documents already in
              the bundle will not appear in search results.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Search Documents"
                value={documentSearchTerm}
                onChange={(e) => setDocumentSearchTerm(e.target.value)}
                placeholder="Enter document name or description..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearchDocuments()}
              />
              <Button
                variant="contained"
                onClick={handleSearchDocuments}
                disabled={!documentSearchTerm.trim() || isSearching}
                sx={{
                  backgroundColor: '#00A859',
                  '&:hover': { backgroundColor: '#008a4a' },
                }}
              >
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </Box>

            {isSearching && <Typography>Searching for documents...</Typography>}

            {hasSearched && searchResults.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Search Results
                </Typography>
                <List>
                  {searchResults.map((document) => (
                    <ListItem
                      key={document.id}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        ...(selectedDocument?.id === document.id && {
                          borderColor: 'primary.main',
                          backgroundColor: 'primary.light',
                        }),
                      }}
                      onClick={() => setSelectedDocument(document)}
                    >
                      <ListItemIcon>
                        <DocumentIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={document.name}
                        secondary={document.description || 'No description'}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {hasSearched &&
              searchResults.length === 0 &&
              documentSearchTerm &&
              !isSearching && (
                <Typography color="text.secondary">
                  No documents found matching "{documentSearchTerm}"
                </Typography>
              )}

            {hasSearched && !documentSearchTerm && !isSearching && (
              <Typography color="text.secondary">
                Enter a search term to find documents
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDocumentModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddDocumentToBundle}
            disabled={!selectedDocument || isAddingDocument}
            sx={{
              backgroundColor: '#00A859',
              '&:hover': { backgroundColor: '#008a4a' },
            }}
          >
            {isAddingDocument ? 'Adding...' : 'Add to Bundle'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentBundle;
